import { getOrElse } from 'fp-ts/lib/Option';
import { useDispatch } from 'react-redux';
import {
  AffinityTrendsCard,
  AffinityTrendsDeltasWrapper,
  AffinityTrendsExportButton,
  AffinityTrendsFlexResponsiveContainer,
  AffinityTrendsFlexRowContainer,
  AffinityTrendsGraphWrapper,
  AffinityTrendsStyledLeftHeader,
  AffinityTrendsStyledRightHeader,
} from 'modules/intelligentInsights/brandAffinityInsights/components/affinityTrends/AffinityTrends.styles';
import { addPositiveToast } from 'modules/toastContainer';
import { SegmentedControl } from 'ui/segmentedControl/SegmentedControl';
import { AffinityTrendsGraph } from 'modules/intelligentInsights/brandAffinityInsights/components/affinityTrends/AffinityTrendsGraph';
import { MarginlessBody2Bold } from 'modules/intelligentInsights/brandAffinityInsights/components/common.styles';
import { AffinityTrendsDeltas } from 'modules/intelligentInsights/brandAffinityInsights/components/affinityTrends/AffinityTrendsDeltas';
import {
  AFFINITY_TREND_CSV_EXPORT_SUCCESS,
  AFFINITY_TRENDS_LEFT_TITLE,
  AFFINITY_TRENDS_RIGHT_TITLE,
} from 'modules/intelligentInsights/brandAffinityInsights/constants';
import { Export16 } from 'ui/icons';
import { BUTTON_VARIANTS } from 'ui/button';
import { downloadAsCsvFileWithHeaders } from 'utils/csv';
import { TrendTypeEnum } from 'modules/intelligentInsights/brandAffinityInsights/types';
import {
  defaultDeltas,
  defaultHistory,
  useDataManagementContext,
} from 'modules/intelligentInsights/brandAffinityInsights/contexts/dataManagement/DataManagementContext';
import { useState } from 'react';
import { createCSVFilename } from 'modules/intelligentInsights/brandAffinityInsights/components/affinityTrends/AffinityTrendsDeltas.utils';
import { useCoreContext } from 'modules/intelligentInsights/brandAffinityInsights/contexts/core/CoreContext';
import { AffinityTrendsSkeleton } from 'modules/intelligentInsights/brandAffinityInsights/components/affinityTrends/AffinityTrendsSkeleton';

export const TrendGraphOptions = [
  {
    name: TrendTypeEnum.Percent,
    value: TrendTypeEnum.Percent,
  },
  {
    name: TrendTypeEnum.Count,
    value: TrendTypeEnum.Count,
  },
];

export const AffinityTrendsBaseCardId = 'affinity-trends-base-card';
export const AffinitTrendsCSVExportButtonId = 'affinity-trends-export-button';

export const AffinityTrendsBase = () => {
  const dispatch = useDispatch();
  const { isReady } = useCoreContext();
  const [graphOption, setGraphOption] = useState(TrendGraphOptions[0].value);
  const {
    getHistoryCSVData,
    managedInsightsData,
    timeSpan: { selectedTimespan },
  } = useDataManagementContext();
  const graphHistory = getOrElse(() => defaultHistory)(managedInsightsData.history);
  const deltas = getOrElse(() => defaultDeltas)(managedInsightsData.deltas);

  const createCSVDataFile = () => {
    const { headers, csvData } = getHistoryCSVData(graphOption);
    downloadAsCsvFileWithHeaders({
      headers,
      data: csvData,
      fileName: createCSVFilename(graphOption, selectedTimespan),
    });
    dispatch(addPositiveToast(AFFINITY_TREND_CSV_EXPORT_SUCCESS));
  };

  return (
    <AffinityTrendsCard data-test={AffinityTrendsBaseCardId}>
      <AffinityTrendsFlexRowContainer>
        <AffinityTrendsStyledLeftHeader>
          {AFFINITY_TRENDS_LEFT_TITLE}
        </AffinityTrendsStyledLeftHeader>
        <AffinityTrendsStyledRightHeader>
          <MarginlessBody2Bold>{AFFINITY_TRENDS_RIGHT_TITLE}</MarginlessBody2Bold>
          <SegmentedControl
            options={TrendGraphOptions}
            onSelect={(nextValue) => {
              setGraphOption(nextValue);
            }}
            value={graphOption}
          />
          <AffinityTrendsExportButton
            data-test={AffinitTrendsCSVExportButtonId}
            onClick={createCSVDataFile}
            variant={BUTTON_VARIANTS.SECONDARY}
          >
            <Export16 />
          </AffinityTrendsExportButton>
        </AffinityTrendsStyledRightHeader>
      </AffinityTrendsFlexRowContainer>
      <AffinityTrendsFlexResponsiveContainer>
        {!isReady ? (
          <AffinityTrendsSkeleton />
        ) : (
          <>
            <AffinityTrendsGraphWrapper>
              <AffinityTrendsGraph
                graphHistory={graphHistory}
                graphTrendType={graphOption}
                selectedTimespan={selectedTimespan}
              />
            </AffinityTrendsGraphWrapper>
            <AffinityTrendsDeltasWrapper>
              <AffinityTrendsDeltas calculatedDeltas={deltas} trendType={graphOption} />
            </AffinityTrendsDeltasWrapper>
          </>
        )}
      </AffinityTrendsFlexResponsiveContainer>
    </AffinityTrendsCard>
  );
};
